import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Scale, Shuffle, Briefcase, Home } from "lucide-react";
import { DYNAMICS } from "../lib/api";

const ICONS = {
  traditional: Home,
  equal_partnership: Scale,
  flexible: Shuffle,
  career_focused: Briefcase,
  homemaker_supported: Heart,
};

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1A1A]">
      <header className="fixed top-0 inset-x-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <span className="font-serif text-2xl tracking-tight" data-testid="brand-logo">
            Evervow
          </span>
          <div className="flex items-center gap-6">
            <Link
              to="/auth?mode=login"
              data-testid="nav-login-link"
              className="text-sm text-[#5C5C5C] hover:text-[#1A1A1A] transition-colors duration-300"
            >
              Sign in
            </Link>
            <Link
              to="/auth?mode=register"
              data-testid="nav-register-link"
              className="bg-[#7A3B3F] text-[#FAF9F6] px-6 py-2.5 text-sm hover:bg-[#5D2B2E] transition-colors duration-300"
            >
              Begin
            </Link>
          </div>
        </div>
      </header>

      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/5760477/pexels-photo-5760477.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800"
            alt="Couple embracing at sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FAF9F6] via-[#FAF9F6]/70 to-transparent" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative max-w-7xl mx-auto px-6 sm:px-12 w-full"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A3B3F] mb-6">
            For those who mean it
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-none max-w-2xl">
            Dating with one destination. Marriage.
          </h1>
          <p className="mt-8 text-lg leading-relaxed text-[#5C5C5C] max-w-xl">
            Evervow is for people done with games. State the partnership you
            actually want — traditional, 50/50, or your own design — and meet
            someone who wants to build the same life.
          </p>
          <div className="mt-12 flex flex-wrap gap-4">
            <button
              data-testid="hero-cta-button"
              onClick={() => navigate("/auth?mode=register")}
              className="bg-[#7A3B3F] text-[#FAF9F6] px-8 py-4 hover:bg-[#5D2B2E] transition-colors duration-300"
            >
              Find your person
            </button>
            <button
              data-testid="hero-signin-button"
              onClick={() => navigate("/auth?mode=login")}
              className="border border-[#1A1A1A] px-8 py-4 hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors duration-300"
            >
              I have an account
            </button>
          </div>
        </motion.div>
      </section>

      <section className="py-24 sm:py-32 bg-[#F3F0EA]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C5C5C] mb-4">
            Your partnership, defined
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight max-w-xl">
            Say what kind of marriage you want, out loud.
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {DYNAMICS.map((d, i) => {
              const Icon = ICONS[d.key];
              return (
                <motion.div
                  key={d.key}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  data-testid={`landing-dynamic-${d.key}`}
                  className="bg-[#FAF9F6] border border-[#E5E0D8] p-8 sm:p-12"
                >
                  <Icon className="w-6 h-6 text-[#7A3B3F]" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl sm:text-2xl mt-6">{d.label}</h3>
                  <p className="mt-3 text-[#5C5C5C] leading-relaxed">{d.desc}</p>
                </motion.div>
              );
            })}
            <div className="bg-[#7A3B3F] text-[#FAF9F6] p-8 sm:p-12 flex flex-col justify-between">
              <h3 className="font-serif text-xl sm:text-2xl">
                No games. No endless swiping. Just intention.
              </h3>
              <button
                data-testid="landing-bottom-cta"
                onClick={() => navigate("/auth?mode=register")}
                className="mt-8 border border-[#FAF9F6] px-6 py-3 self-start hover:bg-[#FAF9F6] hover:text-[#7A3B3F] transition-colors duration-300"
              >
                Begin your story
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-12 border-t border-[#E5E0D8]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col sm:flex-row sm:items-center justify-between gap-6 text-sm text-[#8A8A8A]">
          <p>© {new Date().getFullYear()} Evervow — dating designed to end in marriage.</p>
          <nav className="flex flex-wrap gap-6">
            <Link to="/privacy" data-testid="landing-privacy-link" className="hover:text-[#1A1A1A] transition-colors duration-300">
              Privacy
            </Link>
            <Link to="/terms" data-testid="landing-terms-link" className="hover:text-[#1A1A1A] transition-colors duration-300">
              Terms
            </Link>
            <Link to="/delete-account" data-testid="landing-delete-account-link" className="hover:text-[#1A1A1A] transition-colors duration-300">
              Delete account
            </Link>
            <a href="mailto:support@evervow.me" data-testid="landing-support-link" className="hover:text-[#1A1A1A] transition-colors duration-300">
              support@evervow.me
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
